// ===================================================================
// MAINSTREET — Dashboard
// Overview stats, operational status breakdown, recent activity,
// stale orgs, pending status checks, sector breakdown
// Ported 1:1 from mainstreet-embedded.html renderDashboard()
// ===================================================================

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, SimpleGrid, Group, Stack, Badge, Button,
  Loader, Center,
} from '../../components/gab/GabComponents';
import {
  IconBuilding, IconRefresh, IconArrowRight,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMainStreetStore } from '../../store/mainstreetStore';
import { useMainStreetOpsStore } from '../../store/mainstreetOpsStore';
import { useAuthStore } from '../../store/authStore';
import { SECTOR_TYPES, OP_STATUS, DAMAGE_TYPES } from './constants';
import { semantic } from '../../lib/colors';
import { useIncidentZones } from '../../hooks/useIncidentZones';
import { isInsideZone } from '../../lib/geoopsGeo';

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'var(--color-error)',
  major: 'var(--color-error)',
  moderate: '#C68700',
  minor: 'var(--color-success)',
};

const OP_STATUS_COLORS: Record<string, string> = {
  operational: '#07963F',
  limited: '#A67103',
  closed_temp: semantic.error,
  closed_damage: semantic.error,
  destroyed: semantic.error,
  unknown: '#7B7D7F',
};

function sectorLabel(id: string): string {
  const s = SECTOR_TYPES.find(st => st.id === id);
  return s ? s.label : (id || 'Other').replace(/_/g, ' ');
}

export default function MainStreetDashboard() {
  const navigate = useNavigate();
  const { organizations, facilities, loadOrganizations, loadFacilities } = useMainStreetStore();
  const {
    damageReports, resourceOffers, posts,
    loadDamageReports, loadResourceOffers, loadPosts,
  } = useMainStreetOpsStore();
  const { activeIncident } = useAuthStore();

  const [initialLoad, setInitialLoad] = useState(true);

  const fetchData = useCallback(async () => {
    await Promise.all([
      loadOrganizations(),
      loadFacilities(),
      loadDamageReports(),
      loadResourceOffers(),
      loadPosts(),
    ]);
  }, [loadOrganizations, loadFacilities, loadDamageReports, loadResourceOffers, loadPosts]);

  useEffect(() => {
    fetchData().finally(() => setInitialLoad(false));
  }, [fetchData]);

  // -- Incident-scoped data --
  const incId = activeIncident ? String(activeIncident.id) : null;
  // V2 damage reports and resource offers have no incidentId field — show all
  const incDamageReports = useMemo(() => damageReports, [damageReports]);
  const incResourceOffers = useMemo(() => resourceOffers, [resourceOffers]);
  const incPosts = useMemo(
    () => incId ? posts.filter(p => String(p.incidentId) === incId) : posts,
    [posts, incId],
  );

  // -- Zones --
  const { zones: incidentZones } = useIncidentZones();
  const activeZones = useMemo(() => incidentZones.filter(z => z.status === 'active'), [incidentZones]);
  const orgsInZone = useMemo(() => {
    if (activeZones.length === 0) return [];
    return organizations.filter(o => {
      if (!o.coordinates) return false;
      const parts = o.coordinates.split(',').map(s => parseFloat(s.trim()));
      if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return false;
      return activeZones.some(z => z.geometry && isInsideZone(parts[0], parts[1], z.geometry));
    });
  }, [organizations, activeZones]);

  // -- Stats --
  const totalOrgs = organizations.length;
  const operational = useMemo(() => organizations.filter(o => o.opStatus === 'operational').length, [organizations]);
  const limited = useMemo(() => organizations.filter(o => o.opStatus === 'limited').length, [organizations]);
  const closedOrDamaged = useMemo(
    () => organizations.filter(o => o.opStatus === 'closed_temp' || o.opStatus === 'closed_damage' || o.opStatus === 'destroyed').length,
    [organizations],
  );

  // Stale orgs: not verified in 90 days
  const staleOrgs = useMemo(() => {
    const cutoff = dayjs().subtract(90, 'day');
    return organizations.filter(o => !o.lastVerified || dayjs(o.lastVerified).isBefore(cutoff));
  }, [organizations]);

  // Pending status checks

  // Recent damage reports (sorted by date, top 6)
  const recentDamage = useMemo(
    () => [...incDamageReports].sort((a, b) => (b.reportedAt || '').localeCompare(a.reportedAt || '')).slice(0, 6),
    [incDamageReports],
  );

  // Recent posts (top 5)
  const recentPosts = useMemo(
    () => [...incPosts].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')).slice(0, 5),
    [incPosts],
  );

  // Sector breakdown
  const sectorCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    organizations.forEach(o => {
      const s = o.sectorType || 'other';
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [organizations]);

  // Operational status breakdown for bar chart
  const opCounts = useMemo(() => {
    return OP_STATUS.map(os => ({
      ...os,
      count: organizations.filter(o => o.opStatus === os.id).length,
    })).filter(os => os.count > 0);
  }, [organizations]);

  if (initialLoad) {
    return <Center h={400}><Loader color="#03AC9E" /></Center>;
  }

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb={4}>
        <div>
          <Title order={3} c="black">MainStreet Dashboard</Title>
          <Text size="sm" c="dimmed">
            {activeIncident
              ? `${activeIncident.name} -- Private Sector Coordination`
              : `${totalOrgs} organizations across ${facilities.length} facilities`}
          </Text>
        </div>
        <Button size="xs" variant="light" color="#03AC9E" leftSection={<IconRefresh size={14} />} onClick={() => fetchData()}>
          Refresh
        </Button>
      </Group>

      {/* Stat Cards */}
      <SimpleGrid cols={{ base: 2, sm: 4, md: 7 }} spacing="sm" mb="md">
        <Paper p="xs" radius="sm" withBorder ta="center" style={{ cursor: 'pointer', borderTop: '3px solid #0891b2' }} onClick={() => navigate('/mainstreet/organizations')}>
          <Text size="xs" c="dimmed">Total Orgs</Text>
          <Text fz={24} fw={700}>{totalOrgs}</Text>
        </Paper>

        <Paper p="xs" radius="sm" withBorder ta="center" style={{ cursor: 'pointer', borderTop: '3px solid #07963F' }} onClick={() => navigate('/mainstreet/organizations')}>
          <Text size="xs" c="dimmed">Operational</Text>
          <Text fz={24} fw={700} c="#07963F">{operational}</Text>
        </Paper>

        <Paper p="xs" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid #A67103' }}>
          <Text size="xs" c="dimmed">Limited</Text>
          <Text fz={24} fw={700} c="#A67103">{limited}</Text>
        </Paper>

        <Paper p="xs" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${semantic.error}` }}>
          <Text size="xs" c="dimmed">Closed/Damaged</Text>
          <Text fz={24} fw={700} c={semantic.error}>{closedOrDamaged}</Text>
        </Paper>

        <Paper p="xs" radius="sm" withBorder ta="center" style={{ cursor: 'pointer', borderTop: `3px solid ${semantic.error}` }} onClick={() => navigate('/mainstreet/damage')}>
          <Text size="xs" c="dimmed">Damage Reports</Text>
          <Text fz={24} fw={700} c={semantic.error}>{incDamageReports.length}</Text>
        </Paper>

        <Paper p="xs" radius="sm" withBorder ta="center" style={{ cursor: 'pointer', borderTop: '3px solid #0285C4' }} onClick={() => navigate('/mainstreet/resources')}>
          <Text size="xs" c="dimmed">Resource Offers</Text>
          <Text fz={24} fw={700} c="#0285C4">{incResourceOffers.length}</Text>
        </Paper>

        {activeZones.length > 0 && (
          <Paper p="xs" radius="sm" withBorder ta="center" style={{ cursor: 'pointer', borderTop: '3px solid #B12525' }} onClick={() => navigate('/mainstreet/organizations')}>
            <Text size="xs" c="dimmed">In Impact Zone</Text>
            <Text fz={24} fw={700} c="#B12525">{orgsInZone.length}</Text>
          </Paper>
        )}
      </SimpleGrid>

      {/* Row 1: Status Breakdown + Sector Breakdown */}
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="sm" spacing="sm">
        <Paper p="sm" radius="sm" withBorder>
          <Text fw={600} size="xs" mb="xs">Operational Status</Text>
          <Stack gap={4}>
            {opCounts.map(os => {
              const pct = totalOrgs > 0 ? Math.round((os.count / totalOrgs) * 100) : 0;
              const barColor = OP_STATUS_COLORS[os.id] || '#939598';
              return (
                <Group key={os.id} gap={6} wrap="nowrap">
                  <Text size="xs" c="dimmed" style={{ width: 90, flexShrink: 0, textAlign: 'right' }} truncate>{os.label}</Text>
                  <div style={{ flex: 1, height: 16, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 4, minWidth: os.count > 0 ? 4 : 0, transition: 'width 0.3s' }} />
                  </div>
                  <Text size="xs" fw={700} style={{ width: 28, flexShrink: 0, fontFamily: 'monospace', textAlign: 'right' }}>{os.count}</Text>
                </Group>
              );
            })}
          </Stack>
        </Paper>

        <Paper p="sm" radius="sm" withBorder>
          <Text fw={600} size="xs" mb="xs">Organizations by Sector</Text>
          <Stack gap={4}>
            {sectorCounts.slice(0, 8).map(([sector, count]) => {
              const pct = totalOrgs > 0 ? Math.round((count / totalOrgs) * 100) : 0;
              return (
                <Group key={sector} gap={6} wrap="nowrap">
                  <Text size="xs" c="dimmed" style={{ width: 90, flexShrink: 0, textAlign: 'right' }} truncate>{sectorLabel(sector)}</Text>
                  <div style={{ flex: 1, height: 16, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: '#0891b2', borderRadius: 4, minWidth: count > 0 ? 4 : 0, transition: 'width 0.3s' }} />
                  </div>
                  <Text size="xs" fw={700} style={{ width: 28, flexShrink: 0, fontFamily: 'monospace', textAlign: 'right' }}>{count}</Text>
                </Group>
              );
            })}
            {sectorCounts.length > 8 && <Text size="xs" c="dimmed" ta="center">+ {sectorCounts.length - 8} more</Text>}
          </Stack>
        </Paper>
      </SimpleGrid>

      {/* Row 2: Damage Reports + Stale Orgs */}
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="sm" spacing="sm">
        <Paper p="xs" radius="sm" withBorder>
          <Group justify="space-between" mb={4}>
            <Text fw={600} size="xs">Recent Damage Reports</Text>
            <Button variant="subtle" size="compact-xs" rightSection={<IconArrowRight size={12} />} onClick={() => navigate('/mainstreet/damage')}>All</Button>
          </Group>
          {recentDamage.length === 0 ? (
            <Text size="xs" c="dimmed" ta="center" py="xs">No damage reports yet</Text>
          ) : (
            <Stack gap={3}>
              {recentDamage.map(d => (
                <Group key={d.id} justify="space-between" style={{ borderLeft: `3px solid ${d.severity === 'critical' || d.severity === 'major' ? 'var(--color-error)' : d.severity === 'moderate' ? '#A67103' : '#07963F'}`, paddingLeft: 8 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Text size="xs" fw={600} truncate>{d.orgName || organizations.find(o => String(o.id) === String(d.orgId))?.orgName || 'Unknown'}</Text>
                    <Text size="xs" c="dimmed" truncate>{(d.damageTypes || '').split(',').map(t => t.trim()).filter(Boolean).map(t => DAMAGE_TYPES.find(dt => dt.id === t)?.label ?? t.replace(/_/g, ' ')).join(', ') || ''} — {d.reportedAt ? dayjs(d.reportedAt).format('MMM D') : '--'}</Text>
                  </div>
                  <Badge size="xs" color={SEVERITY_COLORS[d.severity] ?? '#939598'} tt="capitalize">{d.severity || '?'}</Badge>
                </Group>
              ))}
            </Stack>
          )}
        </Paper>

        <Paper p="xs" radius="sm" withBorder>
          <Text fw={600} size="xs" mb={4}>Needs Attention ({staleOrgs.length} stale)</Text>
          {staleOrgs.length === 0 ? (
            <Text size="xs" c="dimmed" ta="center" py="xs">All recently verified</Text>
          ) : (
            <Stack gap={2}>
              {staleOrgs.slice(0, 8).map(o => (
                <Group key={o.id} gap={4} style={{ cursor: 'pointer' }} onClick={() => navigate('/mainstreet/organizations')}>
                  <IconBuilding size={12} style={{ color: '#A67103' }} />
                  <Text size="xs" fw={500} style={{ flex: 1 }} truncate>{o.name}</Text>
                  <Text size="xs" c="dimmed">{o.lastVerified ? dayjs(o.lastVerified).format('MMM D') : 'Never'}</Text>
                </Group>
              ))}
              {staleOrgs.length > 8 && <Text size="xs" c="dimmed" ta="center">+ {staleOrgs.length - 8} more</Text>}
            </Stack>
          )}
        </Paper>
      </SimpleGrid>

      {/* Row 3: Recent Feed (full width) */}
      <Paper p="xs" radius="sm" withBorder mb="sm">
        <Group justify="space-between" mb={4}>
          <Text fw={600} size="xs">Recent Feed Activity</Text>
          <Button variant="subtle" size="compact-xs" rightSection={<IconArrowRight size={12} />} onClick={() => navigate('/mainstreet/feed')}>All</Button>
        </Group>
        {recentPosts.length === 0 ? (
          <Text size="xs" c="dimmed" ta="center" py="xs">No feed activity yet</Text>
        ) : (
          <Stack gap={3}>
            {recentPosts.map(p => (
              <Group key={p.id} gap="xs" style={{ borderLeft: `3px solid ${p.priority === 'critical' ? 'var(--color-error)' : p.priority === 'urgent' ? '#A67103' : '#0891b2'}`, paddingLeft: 8 }}>
                <Text size="xs" fw={600} style={{ minWidth: 100 }}>{p.authorName || '--'}</Text>
                <Text size="xs" c="dimmed" style={{ flex: 1 }} truncate>{p.content}</Text>
                <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>{p.createdAt ? dayjs(p.createdAt).format('MMM D h:mm A') : ''}</Text>
              </Group>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Public Business Registration Link */}
      <Paper p="xs" radius="sm" withBorder>
        <Group justify="space-between">
          <div>
            <Text fw={600} size="sm">Business Registration Portal</Text>
            <Text size="xs" c="dimmed">Share this link so businesses can self-register for the MainStreet portal.</Text>
          </div>
          <Button variant="light" color="#03AC9E" size="xs" onClick={() => {
            const url = `${window.location.origin}/mainstreet/register`;
            navigator.clipboard.writeText(url);
            alert('Registration link copied: ' + url);
          }}>
            Copy Link
          </Button>
        </Group>
      </Paper>
    </div>
  );
}
